import React from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import { LinearProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { groupSubjectApiSlice } from '../../../features/group/groupSubjectSlice'
import { teacherApiSlice } from '../../../features/teacher/teacherSlice'
import { courseApiSlice } from '../../../features/course/courseSlice'
import GroupSubjectsTable from './components/GroupSubjectsTable'
import AddGroupSubjectDialog from './components/AddGroupSubjectDialog'

interface Props {
    courseId: number
    groupName: string
}

const GroupSubjects: React.FC<Props> = ({ courseId, groupName = '' }) => {
    const { formatMessage } = useIntl()
    const { groupId } = useParams()
    const { data: groupSubjects = [], isFetching } =
        groupSubjectApiSlice.useFetchAllGroupSubjectsQuery(Number(groupId))
    const { teachers, isFetchingTeachers } =
        teacherApiSlice.useFetchAllTeachersQuery(
            {
                query: `page=${0}&size=${10000}`,
                roles: ['TEACHER'],
            },
            {
                selectFromResult: ({ data, isFetching }) => ({
                    teachers: data?.data,
                    isFetchingTeachers: isFetching,
                }),
            },
        )
    const { data: course = null, isFetching: isFetchingCourse } =
        courseApiSlice.useGetCourseByIdQuery(courseId)
    const [deleteGroupSubject] =
        groupSubjectApiSlice.useDeleteGroupSubjectMutation()

    const handleDelete = async (id: number) => {
        await toast.promise(deleteGroupSubject(id).unwrap(), {
            pending: formatMessage({ id: 'deleting' }),
            success: formatMessage({ id: 'deletedSuccess' }),
            error: formatMessage({ id: 'deleteFail' }),
        })
    }

    return (
        <Content>
            <Header>
                <Title>
                    <H3>
                        {groupName} {formatMessage({ id: 'subjects' })}
                    </H3>
                </Title>

                <AddGroupSubjectDialog courseId={courseId} />
            </Header>

            {isFetching && <LinearProgress />}

            <GroupSubjectsTable
                data={groupSubjects}
                teachers={teachers}
                course={course}
                isFetching={isFetching}
                isFetchingCourse={isFetchingCourse}
                isFetchingTeachers={isFetchingTeachers}
                onDelete={handleDelete}
            />
        </Content>
    )
}
export default GroupSubjects

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`
